import type { KeyboardEvent } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatConversationDate, formatConversationFallbackTitle } from './formatters';
import type { EmoviDocConversation } from './types';

type EmoviDocConversationItemProps = {
  conversation: EmoviDocConversation;
  isDeleting: boolean;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
};

export function EmoviDocConversationItem({
  conversation,
  isDeleting,
  onSelect,
  onDelete,
}: EmoviDocConversationItemProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(conversation.id);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={conversation.title?.trim() || formatConversationFallbackTitle(conversation)}
      onClick={() => onSelect(conversation.id)}
      onKeyDown={handleKeyDown}
      className={cn(
        'w-full rounded-lg border border-border p-3 text-left transition-colors cursor-pointer',
        'hover:bg-muted/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium truncate">
            {conversation.title?.trim() || formatConversationFallbackTitle(conversation)}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatConversationDate(conversation.lastMessageAt || conversation.createdAt)}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Elimina conversazione"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          isLoading={isDeleting}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDelete(conversation.id);
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
